'use client'

import { useRef, useEffect, useState } from 'react' // Added useEffect
import { motion, useScroll, useTransform } from 'motion/react'
import { cn } from '@lib/utils'
import {
  IconBrightnessDown,
  IconBrightnessUp,
  IconCaretRightFilled,
  IconCaretUpFilled,
  IconChevronUp,
  IconMicrophone,
  IconMoon,
  IconPlayerSkipForward,
  IconPlayerTrackNext,
  IconPlayerTrackPrev,
  IconTable,
  IconVolume,
  IconVolume2,
  IconVolume3
} from '@tabler/icons-react'
import { IconSearch } from '@tabler/icons-react'
import { IconWorld } from '@tabler/icons-react'
import { IconCommand } from '@tabler/icons-react'
import { IconCaretLeftFilled } from '@tabler/icons-react'
import { IconCaretDownFilled } from '@tabler/icons-react'
import Image from 'next/image'
// Adjust the import path based on your actual store location
import { useMacbookStore } from '../../../domain/store/macbookStore' // Added Zustand store import

/**
 * A component that renders an animated MacBook-style interface with scroll effects
 * @component
 * @param {Object} props
 * @param {boolean} [props.showGradient] - Whether to show a gradient overlay at the bottom
 * @param {string} [props.title] - Title text to display above the MacBook
 * @param {ReactNode} [props.badge] - Badge element to display at the bottom left
 * @param {ReactNode} [props.children] - Content to display in the MacBook screen
 *
 * @returns {JSX.Element} A scrollable MacBook interface with animation effects
 *
 * @example
 * <MacbookScroll
 *   src="image.jpg"
 *   showGradient={true}
 *   title="My MacBook"
 * >
 *   <Content />
 * </MacbookScroll>
 */
export const MacbookScroll = ({ showGradient, title, badge, children }) => {
  const ref = useRef(null)
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start']
  })

  // Zustand state for lid and scroll progress
  const isLidExpanded = useMacbookStore(state => state.isLidExpanded)
  // const setIsLidExpanded = useMacbookStore((state) => state.setIsLidExpanded) // Use this if you have a way to toggle
  const setStoreScrollYProgress = useMacbookStore(state => state.setScrollYProgress)

  // Effect to update scrollYProgress in Zustand store
  useEffect(() => {
    const unsubscribe = scrollYProgress.on('change', latest => {
      setStoreScrollYProgress(latest)
    })
    return () => unsubscribe()
  }, [scrollYProgress, setStoreScrollYProgress])

  const textTransform = useTransform(scrollYProgress, [0, 0.3], [0, 100])
  const textOpacity = useTransform(scrollYProgress, [0, 0.2], [1, 0])

  return (
    <div
      ref={ref}
      className={cn(
        'flex min-h-[300vh] shrink-0 flex-col items-center justify-start',
        'md:scale-100 md:py-80 md:[perspective:800px]',
        'sm:scale-50',
        'scale-[0.35] py-0',
        'max-md:scale-100 max-md:py-0 max-md:px-0 max-md:mt-10 max-md:overflow-x-hidden max-md:[perspective:400px]'
      )}
      role='main'
      aria-label='MacBook Display Container'
    >
      <motion.h2
        style={{
          translateY: textTransform,
          opacity: textOpacity
        }}
        className='mb-20 text-center text-3xl font-bold text-neutral-800 dark:text-white'
        role='heading'
        aria-level='2'
      >
        {title}
      </motion.h2>

      {/* Lid now uses isLidExpanded from Zustand store */}
      {/* The onExpand/onCollapse props were removed as Lid component doesn't use them */}
      <Lid expanded={isLidExpanded} scrollYProgress={scrollYProgress}>
        <motion.div
          className='absolute inset-0 h-full rounded-lg object-cover object-left-top mt-8 overflow-hidden'
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            margin: 0,
            padding: 0,
            scale: useTransform(scrollYProgress, [0, 1], [1, 2]),
            opacity: 1
          }}
          role='main'
          aria-label='MacBook Display Content'
        >
          {children}
        </motion.div>
      </Lid>

      <motion.div
        className='relative -z-10 h-[22rem] w-[32rem] overflow-hidden rounded-2xl bg-gray-200 dark:bg-[#272729]'
        style={{
          opacity: useTransform(scrollYProgress, [0.8, 1], [1, 0]),
          pointerEvents: isLidExpanded ? 'none' : 'auto' // Uses isLidExpanded from store
        }}
        role='complementary'
        aria-label='MacBook Base'
      >
        <div className='relative h-10 w-full' role='presentation'>
          <div className='absolute inset-x-0 mx-auto h-4 w-[80%] bg-[#050505]' />
        </div>
        <div className='relative flex' role='group' aria-label='MacBook Input Section'>
          <div className='mx-auto h-full w-[10%] overflow-hidden' role='presentation' aria-hidden='true'>
            <SpeakerGrid />
          </div>
          <div className='mx-auto h-full w-[80%]' role='group' aria-label='Keyboard'>
            <Keypad />
          </div>
          <div className='mx-auto h-full w-[10%] overflow-hidden' role='presentation' aria-hidden='true'>
            <SpeakerGrid />
          </div>
        </div>
        <Trackpad />
        <div
          className='absolute inset-x-0 bottom-0 mx-auto h-2 w-20 rounded-tl-3xl rounded-tr-3xl bg-gradient-to-t from-[#272729] to-[#050505]'
          role='presentation'
          aria-hidden='true'
        />
        {showGradient && (
          <div
            className='absolute inset-x-0 bottom-0 z-50 h-40 w-full bg-gradient-to-t from-white via-white to-transparent dark:from-black dark:via-black'
            role='presentation'
            aria-hidden='true'
          />
        )}
        {badge && (
          <div className='absolute bottom-0 left-4' role='complementary' aria-label='Badge'>
            {badge}
          </div>
        )}
      </motion.div>
    </div>
  )
}

// Lid component definition remains the same, it receives 'expanded' as a prop
export const Lid = ({ expanded, scrollYProgress, className, children }) => {
  return (
    <div className={cn('relative [perspective:800px]', className)} role='region' aria-label='MacBook Display'>
      <LidDisplay scrollYProgress={scrollYProgress} />
      <ScreenDisplay scrollYProgress={scrollYProgress} expanded={expanded}>
        {children}
      </ScreenDisplay>
    </div>
  )
}

// ScreenDisplay receives 'expanded' prop and uses it
const ScreenDisplay = ({ scrollYProgress, expanded, children }) => {
  const screenTransform = {
    scaleX: useTransform(
      scrollYProgress,
      [0, 0.3, 0.6, 1],
      [1.2, expanded ? 1 : 1.5, expanded ? 1 : 1.5, expanded ? 2 : 3] // Uses expanded
    ),
    scaleY: useTransform(
      scrollYProgress,
      [0, 0.3, 0.6, 1],
      [0.6, expanded ? 1 : 1.5, expanded ? 1 : 1.5, expanded ? 1 : 3] // Uses expanded
    ),
    rotateX: useTransform(scrollYProgress, [0.1, 0.12, 0.3, 0.6, 1], [-28, -28, 0, 0, 0]),
    translateY: useTransform(scrollYProgress, [0, 0.6, 1], [0, 1500, 1000]),
    transformStyle: 'preserve-3d',
    transformOrigin: 'top'
  }

  const windowBarTitle = (
    <>
      <span aria-label='Pineapple'>🍍</span>
      Portafolio al <b>metaverso</b>!<span aria-label='Globe'> 🌎</span>
    </>
  )

  return (
    <motion.div
      style={screenTransform}
      className='absolute inset-0 h-96 w-[32rem] rounded-2xl bg-[#010101] p-2'
      role='region'
      aria-label='MacBook Screen Content'
    >
      <WindowBar title={windowBarTitle} />
      <ScreenContent>{children}</ScreenContent>
    </motion.div>
  )
}

// ... rest of your components (LidDisplay, WindowControlButton, etc.) remain unchanged ...
// Ensure all local useState hooks like in Trackpad and KBtn remain as they are,
// as they manage local UI interaction state and moving them to a global store is usually not necessary or beneficial for performance.

// LidDisplay, WindowControlButton, CloseIcon, MinimizeIcon, MaximizeIcon, WindowBar, ScreenContent,
// Trackpad, Keypad, KBtn, SpeakerGrid, OptionKey, ProfileMacbook components follow as before.
// Make sure to include them in the final file. I'm omitting them here for brevity.
// For example:

const LidDisplay = ({ scrollYProgress }) => (
  <div
    style={{
      transform: 'perspective(800px) rotateX(-25deg) translateZ(0px)',
      transformOrigin: 'bottom',
      transformStyle: 'preserve-3d'
    }}
    className='relative h-[12rem] w-[32rem] rounded-2xl bg-[#010101] p-4'
    role='presentation'
  >
    <div
      className='absolute inset-0 flex items-center justify-center rounded-lg bg-[#010101]'
      role='region'
      aria-label='Profile Information Section'
    >
      <motion.div
        className='text-white mb-5'
        style={{
          opacity: useTransform(scrollYProgress, [0, 0.2], [0, 1])
        }}
        role='img'
        aria-label='Profile Photo'
      >
        <ProfileMacbook />
      </motion.div>

      <div role='contentinfo' aria-label='Education Information'>
        <div>
          <motion.h3
            style={{
              opacity: useTransform(scrollYProgress, [0, 0.5], [0, 1]),
              y: useTransform(scrollYProgress, [0, 0.2], [90, 0]),
              fontSize: '14px',
              fontWeight: 'bold'
            }}
            tabIndex={0}
          >
            Alejandro Vega Piña
          </motion.h3>
          <motion.h4
            style={{
              opacity: useTransform(scrollYProgress, [0, 0.3], [0, 1]),
              y: useTransform(scrollYProgress, [0, 0.2], [-90, 0]),
              fontSize: '12px',
              fontWeight: 'normal'
            }}
            tabIndex={0}
          >
            Arquitecto y Desarrollador
          </motion.h4>

          <motion.div
            style={{
              opacity: useTransform(scrollYProgress, [0, 0.2], [0, 1]),
              y: useTransform(scrollYProgress, [0, 0.2], [-90, 0])
            }}
            role='img'
          >
            <Image
              src='/images/udp-192x192.webp'
              alt='Universidad Diego Portales Logo'
              width={100}
              height={100}
              className='object-cover'
            />
          </motion.div>
        </div>

        <motion.h5
          className='text-sm absolute bottom-5 mt-5'
          style={{
            fontSize: '11px',
            opacity: useTransform(scrollYProgress, [0, 0.2], [0, 1]),
            x: useTransform(scrollYProgress, [0, 0.2], [100, 0])
          }}
          tabIndex={0}
        >
          <span aria-hidden='true'>←</span>
          Ingeniería Civil Informática y Telecomunicaciones, 2016
          <span aria-label='Chile'>🇨🇱</span>
          <span aria-hidden='true'>→</span>
        </motion.h5>
      </div>
    </div>
  </div>
)

const WindowControlButton = ({ color, hoverColor, ariaLabel, icon }) => (
  <motion.button
    className={`w-3 h-3 rounded-full flex items-center justify-center cursor-no-drop`}
    style={{ backgroundColor: color }}
    aria-label={ariaLabel}
    whileHover={{ scale: 1.1, backgroundColor: hoverColor }}
    disabled
    aria-disabled='true'
  >
    {icon}
  </motion.button>
)

const CloseIcon = () => (
  <svg viewBox='0 0 10 10' className='w-2 h-2 text-[#4c0102] opacity-0 hover:opacity-100' aria-hidden='true'>
    <path fill='currentColor' d='M1,1 L9,9 M9,1 L1,9' stroke='currentColor' strokeWidth='1.5' />
  </svg>
)

const MinimizeIcon = () => (
  <svg viewBox='0 0 10 10' className='w-2 h-2 text-[#5c4001] opacity-0 hover:opacity-100' aria-hidden='true'>
    <path fill='currentColor' d='M1,5 L9,5' stroke='currentColor' strokeWidth='1.5' />
  </svg>
)

const MaximizeIcon = () => (
  <svg viewBox='0 0 10 10' className='w-2 h-2 text-[#0b4f14] opacity-0 hover:opacity-100' aria-hidden='true'>
    <rect x='2' y='2' width='6' height='6' stroke='currentColor' strokeWidth='1.5' fill='none' />
  </svg>
)

const WindowBar = ({ title }) => (
  <motion.div
    className='absolute top-0 left-0 right-0 h-8 bg-[#2D2D2D] rounded-t-lg flex items-center px-4'
    role='toolbar'
    aria-label='Window Controls'
    whileHover={{
      backgroundColor: 'rgba(45, 45, 45, 0.8)',
      backdropFilter: 'blur(8px)',
      transition: { duration: 0.2 }
    }}
  >
    <div className='flex items-center w-full'>
      <div className='flex space-x-2' role='group' aria-label='Window Buttons'>
        <WindowControlButton color='#FF5F57' hoverColor='#FF1F17' ariaLabel='Close window' icon={<CloseIcon />} />
        <WindowControlButton color='#FEBC2E' hoverColor='#FE9F0E' ariaLabel='Minimize window' icon={<MinimizeIcon />} />
        <WindowControlButton color='#28C840' hoverColor='#08A820' ariaLabel='Maximize window' icon={<MaximizeIcon />} />
      </div>
      <h6 className='text-gray-400 text-xs mx-auto' tabIndex={0}>
        {title}
      </h6>
    </div>
  </motion.div>
)

const ScreenContent = ({ children }) => (
  <>
    <div className='absolute inset-0 rounded-lg bg-[#272729] mt-8' aria-hidden='true' />
    <div
      className='absolute inset-0 h-full rounded-lg object-cover object-left-top mt-8 overflow-hidden cursor-grab active:cursor-grabbing'
      role='main'
    >
      {children || <ProfileMacbook />}
    </div>
  </>
)

export const Trackpad = () => {
  const [isPressed, setIsPressed] = useState(false) // Keep local state for UI interactions

  return (
    <div
      className={cn(
        'mx-auto my-1 h-32 w-[40%] rounded-xl transition-all duration-200',
        'bg-gradient-to-b from-neutral-900 to-black',
        'border border-neutral-700/50',
        isPressed ? 'shadow-inner' : 'shadow-[0_0_2px_1px_rgba(255,255,255,0.1)]',
        isPressed && 'scale-[0.98]'
      )}
      style={{
        boxShadow: isPressed
          ? '0px 0px 1px 1px rgba(0,0,0,0.2) inset'
          : '0px 0px 1px 1px rgba(0,0,0,0.2) inset, 0 0 10px rgba(255,255,255,0.1)'
      }}
      role='region'
      aria-label='MacBook Trackpad'
      tabIndex={0}
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
      onMouseLeave={() => setIsPressed(false)}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          setIsPressed(true)
        }
      }}
      onKeyUp={() => setIsPressed(false)}
    ></div>
  )
}

export const Keypad = () => {
  // Keypad structure remains the same
  return (
    <div className='mx-1 h-full [transform:translateZ(0)] rounded-md bg-[#050505] p-1 [will-change:transform]'>
      {/* First Row */}
      <div className='mb-[2px] flex w-full shrink-0 gap-[2px]'>
        <KBtn className='w-10 items-end justify-start pb-[2px] pl-[4px]' childrenClassName='items-start'>
          esc
        </KBtn>
        <KBtn>
          <IconBrightnessDown className='h-[6px] w-[6px]' />
          <span className='mt-1 inline-block'>F1</span>
        </KBtn>
        <KBtn>
          <IconBrightnessUp className='h-[6px] w-[6px]' />
          <span className='mt-1 inline-block'>F2</span>
        </KBtn>
        <KBtn>
          <IconTable className='h-[6px] w-[6px]' />
          <span className='mt-1 inline-block'>F3</span>
        </KBtn>
        <KBtn>
          <IconSearch className='h-[6px] w-[6px]' />
          <span className='mt-1 inline-block'>F4</span>
        </KBtn>
        <KBtn>
          <IconMicrophone className='h-[6px] w-[6px]' />
          <span className='mt-1 inline-block'>F5</span>
        </KBtn>
        <KBtn>
          <IconMoon className='h-[6px] w-[6px]' />
          <span className='mt-1 inline-block'>F6</span>
        </KBtn>
        <KBtn>
          <IconPlayerTrackPrev className='h-[6px] w-[6px]' />
          <span className='mt-1 inline-block'>F7</span>
        </KBtn>
        <KBtn>
          <IconPlayerSkipForward className='h-[6px] w-[6px]' />
          <span className='mt-1 inline-block'>F8</span>
        </KBtn>
        <KBtn>
          <IconPlayerTrackNext className='h-[6px] w-[6px]' />
          <span className='mt-1 inline-block'>F8</span>
        </KBtn>
        <KBtn>
          <IconVolume3 className='h-[6px] w-[6px]' />
          <span className='mt-1 inline-block'>F10</span>
        </KBtn>
        <KBtn>
          <IconVolume2 className='h-[6px] w-[6px]' />
          <span className='mt-1 inline-block'>F11</span>
        </KBtn>
        <KBtn>
          <IconVolume className='h-[6px] w-[6px]' />
          <span className='mt-1 inline-block'>F12</span>
        </KBtn>
        <KBtn>
          <div className='h-4 w-4 rounded-full bg-gradient-to-b from-neutral-900 from-20% via-black via-50% to-neutral-900 to-95% p-px'>
            <div className='h-full w-full rounded-full bg-black' />
          </div>
        </KBtn>
      </div>
      {/* Second row */}
      <div className='mb-[2px] flex w-full shrink-0 gap-[2px]'>
        <KBtn>
          <span className='block'>~</span>
          <span className='mt-1 block'>`</span>
        </KBtn>
        <KBtn>
          <span className='block'>!</span>
          <span className='block'>1</span>
        </KBtn>
        <KBtn>
          <span className='block'>@</span>
          <span className='block'>2</span>
        </KBtn>
        <KBtn>
          <span className='block'>#</span>
          <span className='block'>3</span>
        </KBtn>
        <KBtn>
          <span className='block'>$</span>
          <span className='block'>4</span>
        </KBtn>
        <KBtn>
          <span className='block'>%</span>
          <span className='block'>5</span>
        </KBtn>
        <KBtn>
          <span className='block'>^</span>
          <span className='block'>6</span>
        </KBtn>
        <KBtn>
          <span className='block'>&</span>
          <span className='block'>7</span>
        </KBtn>
        <KBtn>
          <span className='block'>*</span>
          <span className='block'>8</span>
        </KBtn>
        <KBtn>
          <span className='block'>(</span>
          <span className='block'>9</span>
        </KBtn>
        <KBtn>
          <span className='block'>)</span>
          <span className='block'>0</span>
        </KBtn>
        <KBtn>
          <span className='block'>&mdash;</span>
          <span className='block'>_</span>
        </KBtn>
        <KBtn>
          <span className='block'>+</span>
          <span className='block'> = </span>
        </KBtn>
        <KBtn className='w-10 items-end justify-end pr-[4px] pb-[2px]' childrenClassName='items-end'>
          delete
        </KBtn>
      </div>
      {/* Third row */}
      <div className='mb-[2px] flex w-full shrink-0 gap-[2px]'>
        <KBtn className='w-10 items-end justify-start pb-[2px] pl-[4px]' childrenClassName='items-start'>
          tab
        </KBtn>
        <KBtn>
          <span className='block'>Q</span>
        </KBtn>
        <KBtn>
          <span className='block'>W</span>
        </KBtn>
        <KBtn>
          <span className='block'>E</span>
        </KBtn>
        <KBtn>
          <span className='block'>R</span>
        </KBtn>
        <KBtn>
          <span className='block'>T</span>
        </KBtn>
        <KBtn>
          <span className='block'>Y</span>
        </KBtn>
        <KBtn>
          <span className='block'>U</span>
        </KBtn>
        <KBtn>
          <span className='block'>I</span>
        </KBtn>
        <KBtn>
          <span className='block'>O</span>
        </KBtn>
        <KBtn>
          <span className='block'>P</span>
        </KBtn>
        <KBtn>
          <span className='block'>{`{`}</span>
          <span className='block'>{`[`}</span>
        </KBtn>
        <KBtn>
          <span className='block'>{`}`}</span>
          <span className='block'>{`]`}</span>
        </KBtn>
        <KBtn>
          <span className='block'>{`|`}</span>
          <span className='block'>{`\\`}</span>
        </KBtn>
      </div>
      {/* Fourth Row */}
      <div className='mb-[2px] flex w-full shrink-0 gap-[2px]'>
        <KBtn className='w-[2.8rem] items-end justify-start pb-[2px] pl-[4px]' childrenClassName='items-start'>
          caps lock
        </KBtn>
        <KBtn>
          <span className='block'>A</span>
        </KBtn>
        <KBtn>
          <span className='block'>S</span>
        </KBtn>
        <KBtn>
          <span className='block'>D</span>
        </KBtn>
        <KBtn>
          <span className='block'>F</span>
        </KBtn>
        <KBtn>
          <span className='block'>G</span>
        </KBtn>
        <KBtn>
          <span className='block'>H</span>
        </KBtn>
        <KBtn>
          <span className='block'>J</span>
        </KBtn>
        <KBtn>
          <span className='block'>K</span>
        </KBtn>
        <KBtn>
          <span className='block'>L</span>
        </KBtn>
        <KBtn>
          <span className='block'>{`:`}</span>
          <span className='block'>{`;`}</span>
        </KBtn>
        <KBtn>
          <span className='block'>{`"`}</span>
          <span className='block'>{`'`}</span>
        </KBtn>
        <KBtn className='w-[2.85rem] items-end justify-end pr-[4px] pb-[2px]' childrenClassName='items-end'>
          return
        </KBtn>
      </div>
      {/* Fifth Row */}
      <div className='mb-[2px] flex w-full shrink-0 gap-[2px]'>
        <KBtn className='w-[3.65rem] items-end justify-start pb-[2px] pl-[4px]' childrenClassName='items-start'>
          shift
        </KBtn>
        <KBtn>
          <span className='block'>Z</span>
        </KBtn>
        <KBtn>
          <span className='block'>X</span>
        </KBtn>
        <KBtn>
          <span className='block'>C</span>
        </KBtn>
        <KBtn>
          <span className='block'>V</span>
        </KBtn>
        <KBtn>
          <span className='block'>B</span>
        </KBtn>
        <KBtn>
          <span className='block'>N</span>
        </KBtn>
        <KBtn>
          <span className='block'>M</span>
        </KBtn>
        <KBtn>
          <span className='block'>{`<`}</span>
          <span className='block'>{`,`}</span>
        </KBtn>
        <KBtn>
          <span className='block'>{`>`}</span>
          <span className='block'>{`.`}</span>
        </KBtn>
        <KBtn>
          <span className='block'>{`?`}</span>
          <span className='block'>{`/`}</span>
        </KBtn>
        <KBtn className='w-[3.65rem] items-end justify-end pr-[4px] pb-[2px]' childrenClassName='items-end'>
          shift
        </KBtn>
      </div>
      {/* sixth Row */}
      <div className='mb-[2px] flex w-full shrink-0 gap-[2px]'>
        <KBtn className='' childrenClassName='h-full justify-between py-[4px]'>
          <div className='flex w-full justify-end pr-1'>
            <span className='block'>fn</span>
          </div>
          <div className='flex w-full justify-start pl-1'>
            <IconWorld className='h-[6px] w-[6px]' />
          </div>
        </KBtn>
        <KBtn className='' childrenClassName='h-full justify-between py-[4px]'>
          <div className='flex w-full justify-end pr-1'>
            <IconChevronUp className='h-[6px] w-[6px]' />
          </div>
          <div className='flex w-full justify-start pl-1'>
            <span className='block'>control</span>
          </div>
        </KBtn>
        <KBtn className='' childrenClassName='h-full justify-between py-[4px]'>
          <div className='flex w-full justify-end pr-1'>
            <OptionKey className='h-[6px] w-[6px]' />
          </div>
          <div className='flex w-full justify-start pl-1'>
            <span className='block'>option</span>
          </div>
        </KBtn>
        <KBtn className='w-8' childrenClassName='h-full justify-between py-[4px]'>
          <div className='flex w-full justify-end pr-1'>
            <IconCommand className='h-[6px] w-[6px]' />
          </div>
          <div className='flex w-full justify-start pl-1'>
            <span className='block'>command</span>
          </div>
        </KBtn>
        <KBtn className='w-[8.2rem]'></KBtn>
        <KBtn className='w-8' childrenClassName='h-full justify-between py-[4px]'>
          <div className='flex w-full justify-start pl-1'>
            <IconCommand className='h-[6px] w-[6px]' />
          </div>
          <div className='flex w-full justify-start pl-1'>
            <span className='block'>command</span>
          </div>
        </KBtn>
        <KBtn className='' childrenClassName='h-full justify-between py-[4px]'>
          <div className='flex w-full justify-start pl-1'>
            <OptionKey className='h-[6px] w-[6px]' />
          </div>
          <div className='flex w-full justify-start pl-1'>
            <span className='block'>option</span>
          </div>
        </KBtn>
        <div className='mt-[2px] flex h-6 w-[4.9rem] flex-col items-center justify-end rounded-[4px] p-[0.5px]'>
          <KBtn className='h-3 w-6'>
            <IconCaretUpFilled className='h-[6px] w-[6px]' />
          </KBtn>
          <div className='flex'>
            <KBtn className='h-3 w-6'>
              <IconCaretLeftFilled className='h-[6px] w-[6px]' />
            </KBtn>
            <KBtn className='h-3 w-6'>
              <IconCaretDownFilled className='h-[6px] w-[6px]' />
            </KBtn>
            <KBtn className='h-3 w-6'>
              <IconCaretRightFilled className='h-[6px] w-[6px]' />
            </KBtn>
          </div>
        </div>
      </div>
    </div>
  )
}

export const KBtn = ({ className, children, childrenClassName, backlit = true }) => {
  const [isPressed, setIsPressed] = useState(false) // Keep local state

  return (
    <motion.div
      role='button'
      tabIndex={0}
      aria-pressed={isPressed}
      aria-label={typeof children === 'string' ? children : 'Keyboard key'}
      className={cn(
        '[transform:translateZ(0)] rounded-[4px] p-[0.5px] [will-change:transform] transition-all duration-100',
        backlit && 'bg-white/[0.5] shadow-xl shadow-white',
        isPressed && 'scale-95',
        'cursor-pointer'
      )}
      whileHover={{
        boxShadow: '0 0 8px #FFE135, 0 0 12px #FFE135, 0 0 16px #FFE135',
        transition: { duration: 0.2 }
      }}
      onMouseDown={() => {
        setIsPressed(true)
      }}
      onMouseUp={() => setIsPressed(false)}
      onMouseLeave={() => setIsPressed(false)}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          setIsPressed(true)
        }
      }}
      onKeyUp={() => setIsPressed(false)}
    >
      <motion.div
        className={cn(
          'flex h-6 w-6 items-center justify-center rounded-[3.5px] bg-[#0A090D] transition-all duration-100',
          className,
          isPressed && 'bg-[#1A191D]'
        )}
        style={{
          boxShadow: isPressed ? 'none' : '0px -0.5px 2px 0 #0D0D0F inset, -0.5px 0px 2px 0 #0D0D0F inset'
        }}
        whileHover={{
          backgroundColor: '#2A2922',
          transition: { duration: 0.2 }
        }}
      >
        <motion.div
          className={cn(
            'flex w-full flex-col items-center justify-center text-[5px] transition-all duration-100',
            childrenClassName,
            backlit && 'text-white',
            isPressed && 'text-yellow-400 scale-110'
          )}
          whileHover={{
            color: '#FFE135',
            textShadow: '0 0 5px #FFE135',
            scale: 1.1,
            transition: { duration: 0.2 }
          }}
        >
          {children}
        </motion.div>
      </motion.div>
    </motion.div>
  )
}

export const SpeakerGrid = () => {
  return (
    <div
      className='mt-2 flex h-40 gap-[2px] px-[0.5px]'
      style={{
        backgroundImage: 'radial-gradient(circle, #08080A 0.5px, transparent 0.5px)',
        backgroundSize: '3px 3px'
      }}
      role='presentation'
      aria-hidden='true'
      aria-label='Speaker grill decoration'
    />
  )
}

export const OptionKey = ({ className }) => {
  return (
    <svg
      fill='none'
      version='1.1'
      id='icon'
      xmlns='http://www.w3.org/2000/svg'
      viewBox='0 0 32 32'
      className={className}
    >
      <rect stroke='currentColor' strokeWidth={2} x='18' y='5' width='10' height='2' />
      <polygon stroke='currentColor' strokeWidth={2} points='10.6,5 4,5 4,7 9.4,7 18.4,27 28,27 28,25 19.6,25 ' />
      <rect id='_Transparent_Rectangle_' className='st0' width='32' height='32' stroke='none' />
    </svg>
  )
}

const ProfileMacbook = () => {
  return (
    <Image
      src={'/images/profile-192x192.webp'}
      alt='Profile photo of the developer'
      width={192}
      height={192}
      className='object-cover object-center'
      priority
      loading='eager'
      role='img'
      aria-label='Profile photo of the developer'
    />
  )
}
